import React, { useState, useEffect } from 'react';
import StatusFlow from './StatusFlow';
import ItemEditComponent from '../../components/TextEdit';
import SwitchEdit from '../../components/SwitchEdit';
import Select from '../../components/SelectObjComponent';
import TemplateSelector from '../../components/TemplateSelector';
import '../../styles/page-common.css';

// Lightweight swatch picker — replaces react-color's CirclePicker (react-color
// + its @icons/material dep landed in the PDF/canvas vendor chunk that throws
// 'process is not defined', which crashed this page). Same onChangeComplete({hex}) API.
const SWATCHES = ['#0DA1B2', '#067886', '#2563EB', '#7C5CFC', '#1B9F69', '#E5A000', '#DA4A4F', '#0f1f2e', '#E91E63', '#9C27B0', '#3F51B5', '#00BCD4', '#FF7043', '#8BC34A', '#6c757d'];
const ColorSwatches = ({ color, onChangeComplete }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxWidth: 196 }}>
    {SWATCHES.map((c) => (
      <button
        key={c}
        type="button"
        title={c}
        onClick={() => onChangeComplete({ hex: c })}
        style={{
          width: 24, height: 24, borderRadius: '50%', background: c, cursor: 'pointer', padding: 0,
          border: (color || '').toLowerCase() === c.toLowerCase() ? '2px solid #0f1f2e' : '1px solid rgba(15,31,46,0.15)',
          boxShadow: (color || '').toLowerCase() === c.toLowerCase() ? '0 0 0 2px rgba(255,255,255,0.9) inset' : 'none',
        }}
      />
    ))}
  </div>
);

const RenderView = ({
  templates,
  invoiceTemplates,
  dispatchCreateAppointmentTemplate,
  company
}) => {


  // Saved templates can lack statuses/components entirely — every editor code
  // path assumes both arrays exist, so normalize at the source.
  const normalize = (ts) => (ts || []).map((t) => ({ ...t, statuses: t.statuses || [], components: t.components || [] }));

  const [selectedTempIndex, setSelectedTempIndex] = useState(templates && templates.length > 0 ? 0 : null);

  const [newTemplates, setNewTemplates] = useState(normalize(templates));
  const [selectedStatus, setSelectedStatus] = useState();
  const [fakeObject, setFakeObject] = useState({});
  // Select components BY INDEX — the old object-reference + find-by-prop
  // approach silently edited the WRONG component (stale reference / duplicate
  // prop names made `find(i => i.prop == selected.prop)` hit another field).
  const [selectedCompIndex, setSelectedCompIndex] = useState(null);
  const [showStatusSettings, setShowStatusSettings] = useState(false);
  const [showTemplateSettings, setShowTemplateSettings] = useState(false);
  const [selectedUserGroup, setSelectedUserGroup] = useState();

  // On a fresh page load the redux templates arrive AFTER first mount —
  // re-seed the local editing state when they do (the old useState-only
  // seeding left the editor permanently empty on direct loads).
  useEffect(() => {
    setNewTemplates(normalize(templates));
    setSelectedTempIndex(templates && templates.length > 0 ? 0 : null);
    setSelectedStatus();
    setSelectedCompIndex(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  const components = ['clientSelect', 'text', 'textArea', 'date', 'switch', 'invoice', 'document', 'wording', 'select', 'itemsSelect', 'usersSelect', 'userSigniture', 'signiture', 'comments', 'images'];

  // StatusFlow emits the updated statuses (with .draw positions + .next links)
  // for the selected template; persist them into local state.
  const updateStatuses = (statuses) => {
    let nTemplate = newTemplates[selectedTempIndex];
    nTemplate.statuses = statuses;
    setNewTemplates([...newTemplates]);
  };

  const handleStatusColorChange = (color) => {
    let nTemplate = newTemplates[selectedTempIndex];
    let t = nTemplate.statuses.find((i, k) => k == selectedStatus);
    if (t) {
      t.color = color.hex;
      setNewTemplates([...newTemplates]);
    }
  };

  const onChangeFakeChange = (key, value) => {
    let nTemplate = { ...fakeObject }
    nTemplate[key] = value;

    setFakeObject(nTemplate);
  }

  const selectedComponent = (selectedCompIndex != null
    && newTemplates && newTemplates[selectedTempIndex]
    && newTemplates[selectedTempIndex].components[selectedCompIndex]) || null;

  const onSetSelectedComponent = (index) => {
    setSelectedCompIndex(index);
  }

  const onChangeStatusText = (key, value) => {
    let nTemplate = newTemplates[selectedTempIndex];

    let t = nTemplate.statuses.find((i, k) => k == selectedStatus);
    if (t) {
      t[key] = value;
      setNewTemplates([...newTemplates]);
    }
  }

  const onChangeComponentText = (key, value) => {
    let nTemplate = newTemplates[selectedTempIndex];

    let t = nTemplate.components[selectedCompIndex];
    if (t) {
      t[key] = value;
      setNewTemplates([...newTemplates]);
    }
  }

  const onChangeComponentOptions = (key, value) => {
    let nTemplate = newTemplates[selectedTempIndex];

    let t = nTemplate.components[selectedCompIndex];
    if (t) {
      if (!t.permissions) t.permissions = {};

      if (selectedStatus >= 0 && selectedUserGroup) {
        let status = newTemplates[selectedTempIndex].statuses[selectedStatus].id;
        if (!t.permissions[status]) t.permissions[status] = {};
        if (!t.permissions[status][selectedUserGroup]) t.permissions[status][selectedUserGroup] = {};
        t.permissions[status][selectedUserGroup][key] = value;
      }
      else if (selectedStatus >= 0 && !selectedUserGroup) {
        let status = newTemplates[selectedTempIndex].statuses[selectedStatus].id;
        if (!t.permissions[status]) t.permissions[status] = {};
        t.permissions[status][key] = value;
      }
      else {
        t.permissions[key] = value;
      }
      setNewTemplates([...newTemplates]);
    }
  }

  const onChangeStatusOptions = (key, value) => {
    let nTemplate = newTemplates[selectedTempIndex];

    let t = nTemplate.statuses[selectedStatus];
    if (t) {
      if (!t.permissions) t.permissions = {};

      if (selectedUserGroup) {
        if (!t.permissions[selectedUserGroup]) t.permissions[selectedUserGroup] = {};
        t.permissions[selectedUserGroup][key] = value;
      }
      else {
        t.permissions[key] = value;
      }

      setNewTemplates([...newTemplates]);
    }
  }

  const handleColorChange = (color) => {
    let nTemplate = newTemplates[selectedTempIndex];
    nTemplate.color = color.hex;
    setNewTemplates([...newTemplates]);
  };

  const onChangeText = (key, value) => {
    let nTemplate = newTemplates[selectedTempIndex];

    nTemplate[key] = value;
    setNewTemplates([...newTemplates]);
  }

  let status_permissions = (selectedStatus >= 0 && newTemplates[selectedTempIndex].statuses[selectedStatus].permissions) ? newTemplates[selectedTempIndex].statuses[selectedStatus].permissions : {};
  if (selectedUserGroup && status_permissions[selectedUserGroup]) {
    status_permissions = status_permissions[selectedUserGroup];
  }

  let permissions = (selectedComponent && selectedComponent.permissions) ? selectedComponent.permissions : {};

  if (selectedComponent && selectedTempIndex >= 0 && selectedUserGroup) {
    let status = newTemplates[selectedTempIndex].statuses[selectedStatus];
    if (status) {
      status = status.id;
      if (!selectedComponent.permissions) selectedComponent.permissions = {};
      if (!selectedComponent.permissions[status]) selectedComponent.permissions[status] = {};
      if (!selectedComponent.permissions[status][selectedUserGroup]) selectedComponent.permissions[status][selectedUserGroup] = {};
      permissions = selectedComponent.permissions[status][selectedUserGroup];
    }
  }
  else if (selectedComponent && selectedTempIndex >= 0 && !selectedUserGroup) {
    let status = newTemplates[selectedTempIndex].statuses[selectedStatus];
    if (status) {
      status = status.id;
      if (!selectedComponent.permissions) selectedComponent.permissions = {};
      if (!selectedComponent.permissions[status]) selectedComponent.permissions[status] = {};
      permissions = selectedComponent.permissions[status];
    }
  }

  let selectedTemplateStatus;
  if (selectedStatus >= 0) {
    selectedTemplateStatus = newTemplates[selectedTempIndex].statuses.find((i, k) => k == selectedStatus);
  }

  return (<div className="content-wrapper">

    {/* Page Header */}
    <div className="page-header">
      <h1 className="page-title">
        <i className="fas fa-calendar-check"></i>
        Appointment Settings
      </h1>
    </div>

    <div style={{ padding: '0 20px 20px 20px' }}>

    {/* Toolbar: template pills + settings + Save All. Inline-styled buttons —
        the legacy .btn-app class is display:none'd globally by the invoices/
        expenses/entities cleanup CSS, which made this whole toolbar invisible. */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
      {newTemplates && newTemplates.length > 0 && newTemplates.map((i, k) => {
        const accent = i.color || '#0DA1B2';
        const sel = selectedTempIndex == k;
        return (
          <button
            key={k}
            type="button"
            onClick={() => {
              setSelectedStatus();
              setSelectedCompIndex(null);
              setSelectedTempIndex(k);
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px',
              borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13,
              fontFamily: 'var(--font-display)',
              background: sel ? accent : 'rgba(255,255,255,0.72)',
              color: sel ? '#fff' : 'var(--text)',
              border: sel ? '1px solid transparent' : '1px solid rgba(15,31,46,0.12)',
              boxShadow: sel ? '0 4px 14px rgba(15,31,46,0.18)' : 'none',
            }}>
            <i className={i.icon ? i.icon : "fas fa-calendar-check"}></i> {i.name}
          </button>);
      })}

      <button type="button" onClick={() => {
        newTemplates.push({
          description: "New description",
          statuses: [],
          components: [],
          name: "New"
        })
        setNewTemplates([...newTemplates]);
      }} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
        borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13,
        fontFamily: 'var(--font-display)', background: 'transparent',
        color: 'var(--text-secondary)', border: '1px dashed rgba(15,31,46,0.25)',
      }}>
        <i className="fas fa-plus"></i> New template
      </button>

      <span style={{ flex: 1 }}></span>

      {newTemplates && newTemplates.length > 0 && selectedTempIndex >= 0 && (
        <button type="button" onClick={() => setShowTemplateSettings(!showTemplateSettings)} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
          borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 13,
          background: showTemplateSettings ? 'rgba(15,31,46,0.08)' : 'rgba(255,255,255,0.72)',
          color: 'var(--text-secondary)', border: '1px solid rgba(15,31,46,0.12)',
        }}>
          <i className="fas fa-cog"></i> Template settings
        </button>
      )}

      <button type="button" onClick={() => {
        newTemplates.forEach(template => {
          dispatchCreateAppointmentTemplate(template)
        });
        setNewTemplates([...newTemplates]);
      }} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 18px',
        borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13,
        fontFamily: 'var(--font-display)', border: 'none', color: '#fff',
        background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))',
        boxShadow: '0 6px 16px rgba(13,161,178,0.35)',
      }}>
        <i className="fas fa-save"></i> Save All
      </button>
    </div>

    {/* Template settings panel */}
    {showTemplateSettings && newTemplates && newTemplates.length > 0 && selectedTempIndex >= 0 && (
      <div style={{ marginBottom: 12, background: 'rgba(255,255,255,0.82)', border: '1px solid var(--glass-border)', padding: 16, borderRadius: 14, maxWidth: 520 }}>
        <ItemEditComponent key={'name'} header={'Name'} onChangeText={onChangeText} prop={'name'} value={newTemplates[selectedTempIndex].name} />
        <ItemEditComponent key={'description'} header={'Description'} onChangeText={onChangeText} prop={'description'} value={newTemplates[selectedTempIndex].description} />
        <ItemEditComponent key={'icon'} header={'Icon'} onChangeText={onChangeText} prop={'icon'} value={newTemplates[selectedTempIndex].icon ? newTemplates[selectedTempIndex].icon : 'fas fa-heart'} />

        <div style={{ padding: 10 }}><ColorSwatches
          color={newTemplates[selectedTempIndex].color}
          onChangeComplete={handleColorChange} /></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowTemplateSettings(false)}><i className="fas fa-times" style={{ marginRight: 6 }}></i>Close</button>
          <button type="button" className="btn btn-sm" style={{ background: 'rgba(218,74,79,0.10)', border: '1px solid rgba(218,74,79,0.30)', color: 'var(--danger)' }} onClick={() => {
            let index = selectedTempIndex;
            setSelectedStatus();
            setSelectedTempIndex(0);
            let n = [...newTemplates];
            n.splice(index, 1)
            setNewTemplates(n);
          }}><i className="fas fa-ban" style={{ marginRight: 6 }}></i>Delete template</button>
        </div>
      </div>
    )}

    {newTemplates && newTemplates.length > 0 && selectedTempIndex >= 0 && (
      <StatusFlow
        statuses={newTemplates[selectedTempIndex].statuses || []}
        selectedIndex={selectedStatus}
        onSelect={setSelectedStatus}
        onChange={updateStatuses}
      />
    )}

    {newTemplates && newTemplates.length > 0 && selectedTempIndex >= 0 && <div style={{ marginRight: 10, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}>
      <div style={{ marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }} >
        {newTemplates[selectedTempIndex].statuses.map((i, k) => {
          const accent = i.color || '#6c757d';
          const sel = selectedStatus == k;
          return (
            <button key={k} type="button" onClick={() => setSelectedStatus(k)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
              borderRadius: 9, cursor: 'pointer', fontWeight: 600, fontSize: 12,
              background: sel ? accent : 'rgba(255,255,255,0.72)',
              color: sel ? '#fff' : 'var(--text-secondary)',
              border: sel ? '1px solid transparent' : '1px solid rgba(15,31,46,0.12)',
            }}>
              <i className="fas fa-circle" style={{ fontSize: 7, color: sel ? '#fff' : accent }}></i> {i.name}
            </button>)
        }
        )}
      </div>

      <div style={{ marginRight: 10, marginBottom: 10, display: 'flex', backgroundColor: 'white', padding: 10, borderRadius: 4 }}>
        {!showStatusSettings && selectedStatus >= 0 && <a className={"btn"} onClick={() => setShowStatusSettings(!showStatusSettings)}><i className={"fas fa-cog"}></i> {'settings'}</a>}

        {showStatusSettings && selectedStatus >= 0 && <div style={{}}>
          <ItemEditComponent key={'name'} header={'Name'} onChangeText={onChangeStatusText} prop={'name'} value={selectedTemplateStatus.name} />
          <ItemEditComponent key={'id_status'} header={'Id'} onChangeText={onChangeStatusText} prop={'id'} value={selectedTemplateStatus.id} />
          <ItemEditComponent key={'icon'} header={'Icon'} onChangeText={onChangeStatusText} prop={'icon'} value={selectedTemplateStatus.icon ? selectedTemplateStatus.icon : 'fas fa-heart'} />
          <div style={{ padding: 10 }}><ColorSwatches
            color={selectedTemplateStatus.color}
            onChangeComplete={handleStatusColorChange} /></div>

          <div style={{ border: '1px solid #ced4da', padding: 5 }}>
            <SwitchEdit key={'autoGenerateInvoice'} header={'Auto Generate Invoice'} onChangeText={onChangeStatusText} prop={'autoGenerateInvoice'} value={selectedTemplateStatus.autoGenerateInvoice} />
            {selectedTemplateStatus.autoGenerateInvoice && <div>
                <Select key={'autoSendInvoiceTemplate'} header={'Invoice Template'} options={invoiceTemplates.map(i => {
                  return {
                    value: i._id,
                    label: i.invoiceTemplateName
                  }
                })} onChangeText={onChangeStatusText} prop={'autoSendInvoiceTemplate'} value={selectedTemplateStatus.autoSendInvoiceTemplate} />

              <SwitchEdit key={'autoSendInvoiceClient'} header={'Send Invoice to Client'} onChangeText={onChangeStatusText} prop={'autoSendInvoiceClient'} value={selectedTemplateStatus.autoSendInvoiceClient} />
              <Select key={'Repeat'} header={'Frequency'} options={['creation', 'daily', 'weekly', 'monthly']} onChangeText={onChangeStatusText} prop={'autoSendInvoiceClientFrequency'} value={selectedTemplateStatus.autoSendInvoiceClientFrequency} />
              <SwitchEdit key={'autoMoveInvoiceClient'} header={'Move to next status'} onChangeText={onChangeStatusText} prop={'autoMoveInvoiceClient'} value={selectedTemplateStatus.autoMoveInvoiceClient} />
              <SwitchEdit key={'autoGroupInvoiceClient'} header={'Group Per Client'} onChangeText={onChangeStatusText} prop={'autoGroupInvoiceClient'} value={selectedTemplateStatus.autoGroupInvoiceClient} />
              {newTemplates[selectedTempIndex].components.filter(a => a.type === 'document').map((c, k) => {
                  return (
                    <SwitchEdit
                      key={'autoSendInvoiceClientIncludeTemplates'+k}
                      header={c.label}
                      onChangeText={(templateId, val) => {
                        let sendNotificationTemplates = [];
                        if (!selectedTemplateStatus.autoSendInvoiceClientIncludeTemplates) {
                          if (val) {
                            sendNotificationTemplates = [templateId];
                          }
                        }
                        else {
                          if (!val) {
                            sendNotificationTemplates = selectedTemplateStatus.autoSendInvoiceClientIncludeTemplates.filter(t => t.id != templateId);
                          }
                          else {
                            sendNotificationTemplates = selectedTemplateStatus.autoSendInvoiceClientIncludeTemplates.filter(t => t.id != templateId);
                            sendNotificationTemplates.push({ id: templateId, name: c.label });
                          }
                        }
                        onChangeStatusText('autoSendInvoiceClientIncludeTemplates', sendNotificationTemplates);
                      }} prop={c.templateId}
                      value={selectedTemplateStatus.autoSendInvoiceClientIncludeTemplates?.find(t => t.name)} />)
                })}

            </div>}
          </div>

          <div style={{ border: '1px solid #ced4da', padding: 5 }}>
            <SwitchEdit key={'sendNotification'} header={'Send Notification'} onChangeText={onChangeStatusText} prop={'sendNotification'} value={selectedTemplateStatus.sendNotification} />
            {selectedTemplateStatus.sendNotification && <div>
              <SwitchEdit key={'sendNotificationClient'} header={'Send Notification to Client'} onChangeText={onChangeStatusText} prop={'sendNotificationClient'} value={selectedTemplateStatus.sendNotificationClient} />
              <ItemEditComponent key={'sendNotificationSubject'} header={'Subject'} onChangeText={onChangeStatusText} prop={'sendNotificationSubject'} value={selectedTemplateStatus.sendNotificationSubject} />
              <ItemEditComponent multiline={3} key={'sendNotificationBody'} header={'Body'} onChangeText={onChangeStatusText} prop={'sendNotificationBody'} value={selectedTemplateStatus.sendNotificationBody} />
              {newTemplates && newTemplates.length > 0 && selectedTempIndex >= 0 && <div style={{ padding: 10, display: 'flex', flexDirection: 'column' }}>
                {newTemplates[selectedTempIndex].components.filter(a => a.type === 'invoice' || a.type === 'document').map((c, k) => {
                  if (c.type === 'invoice') return (<SwitchEdit key={'sendNotificationInvoice'} header={'Include Invoice'} onChangeText={onChangeStatusText} prop={'sendNotificationInvoice'} value={selectedTemplateStatus.sendNotificationInvoice} />)
                  else return (
                    <SwitchEdit
                      key={'sendNotificationTemplates'}
                      header={c.label}
                      onChangeText={(templateId, val) => {
                        let sendNotificationTemplates = [];
                        if (!selectedTemplateStatus.sendNotificationTemplates) {
                          if (val) {
                            sendNotificationTemplates = [templateId];
                          }
                        }
                        else {
                          if (!val) {
                            sendNotificationTemplates = selectedTemplateStatus.sendNotificationTemplates.filter(t => t.id != templateId);
                          }
                          else {
                            sendNotificationTemplates = selectedTemplateStatus.sendNotificationTemplates.filter(t => t.id != templateId);
                            sendNotificationTemplates.push({ id: templateId, name: c.label });
                          }
                        }
                        onChangeStatusText('sendNotificationTemplates', sendNotificationTemplates);
                      }} prop={c.templateId}
                      value={selectedTemplateStatus.sendNotificationTemplates?.find(t => t.name)} />)
                })}
                <Select key={'sendNotificationInvoiceTemplate'} header={'Invoice Template'} options={invoiceTemplates.map(i => {
                  return {
                    value: i._id,
                    label: i.invoiceTemplateName
                  }
                })} onChangeText={onChangeStatusText} prop={'sendNotificationInvoiceTemplate'} value={selectedTemplateStatus.sendNotificationInvoiceTemplate} />
              </div>}
            </div>}
          </div>

          <div style={{ fontSize: 20, paddingBottom: 10, paddingTop: 10, fontWeight: 500 }}>{'Options'}</div>
          <div>
            <SwitchEdit key={'hideDeleteItem'} header={'Hide Delete Item'} onChangeText={onChangeStatusOptions} prop={'hideDeleteItem'} value={status_permissions.hideDeleteItem} />
            <SwitchEdit key={'viewOnlySchedule'} header={'View Only Schedule'} onChangeText={onChangeStatusOptions} prop={'viewOnlySchedule'} value={status_permissions.viewOnlySchedule} />
            <SwitchEdit key={'hideAssignee'} header={'Hide Assignee'} onChangeText={onChangeStatusOptions} prop={'hideAssignee'} value={status_permissions.hideAssignee} />
          </div>


          <a className={"btn btn-info"} onClick={() => setShowStatusSettings(!showStatusSettings)}><i className={"fas fa-times"}></i> {'Close'}</a>
        </div>}
      </div>
    </div>}


    {company && company.userGroups && <Select key={'userGroup'} header={'User Group'} options={Object.keys(company.userGroups)} onChangeText={(key, value) => setSelectedUserGroup(value != 'Please select' ? value : null)} prop={'type'} value={selectedUserGroup} />}

    {newTemplates && newTemplates.length > 0 && selectedTempIndex >= 0 && <div style={{ padding: 10, display: 'flex' }}>
      <div style={{ marginRight: 10, backgroundColor: 'white', padding: 10, borderRadius: 4 }} >
        <div style={{ fontSize: 20, paddingBottom: 10, fontWeight: 500 }}>{'Items'}</div>
        <a className={"btn btn-info"} onClick={() => {
          let nTemplate = newTemplates[selectedTempIndex];
          nTemplate.components.push({
            prop: 'prop' + nTemplate.components.length,
            label: 'New',
            type: 'text'
          })
          setNewTemplates([...newTemplates]);
        }}><i className={"fas fa-plus"}></i> {'Add'}</a>
        {newTemplates[selectedTempIndex].components.map((c, k) => {
          const sel = selectedCompIndex === k;
          return (<div key={'comp' + k} onClick={() => {
            onSetSelectedComponent(k);
          }} style={{ padding: 10, borderWidth: 1, borderStyle: 'solid', borderColor: sel ? 'var(--primary)' : '#ced4da', background: sel ? 'rgba(13,161,178,0.08)' : 'transparent', cursor: 'pointer' }}>{c.label}</div>)
        })}
      </div>
      {selectedComponent && <div style={{ marginRight: 10, backgroundColor: 'white', padding: 10, borderRadius: 4 }} >
        <div style={{ fontSize: 20, paddingBottom: 10, fontWeight: 500 }}>{'Properties'}</div>
        <ItemEditComponent key={'prop'} header={'Property'} onChangeText={onChangeComponentText} prop={'prop'} value={selectedComponent.prop} />
        <ItemEditComponent key={'label'} header={'Display Label'} onChangeText={onChangeComponentText} prop={'label'} value={selectedComponent.label} />
        <Select key={'type'} header={'Type'} options={components} onChangeText={onChangeComponentText} prop={'type'} value={selectedComponent.type} />

        {selectedComponent.type == 'select' && <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Dropdown options <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(one per line)</span></div>
          <textarea
            key={'opts' + selectedCompIndex}
            className="form-control"
            rows={4}
            defaultValue={(selectedComponent.options || []).join('\n')}
            placeholder={'Option 1\nOption 2\nOption 3'}
            onBlur={(e) => {
              const opts = e.target.value.split('\n').map(s => s.trim()).filter(Boolean);
              onChangeComponentText('options', opts);
            }}
          />
        </div>}

        <div style={{ fontSize: 20, paddingBottom: 10, paddingTop: 10, fontWeight: 500 }}>{'Options'}</div>
        {selectedComponent.type == 'itemsSelect' && <div>
          <SwitchEdit key={'hideItemCode'} header={'Hide Item Code'} onChangeText={onChangeComponentOptions} prop={'hideItemCode'} value={permissions.hideItemCode} />
          <SwitchEdit key={'hideItemPrice'} header={'Hide Item Price'} onChangeText={onChangeComponentOptions} prop={'hideItemPrice'} value={permissions.hideItemPrice} />
          <SwitchEdit key={'hideItemTax'} header={'Hide Item Tax'} onChangeText={onChangeComponentOptions} prop={'hideItemTax'} value={permissions.hideItemTax} />
          <SwitchEdit key={'hideAccount'} header={'Hide Account'} onChangeText={onChangeComponentOptions} prop={'hideAccount'} value={permissions.hideAccount} />
          <SwitchEdit key={'hideSaveItem'} header={'Hide Save Item'} onChangeText={onChangeComponentOptions} prop={'hideSaveItem'} value={permissions.hideSaveItem} />
        </div>}

        {selectedComponent.type == 'images' && <div>
          <SwitchEdit key={'allowMultiImages'} header={'Allow Multiple Images'} onChangeText={onChangeComponentOptions} prop={'allowMultiImages'} value={permissions.allowMultiImages} />
        </div>}

        <SwitchEdit key={'viewOnly'} header={'View Only'} onChangeText={onChangeComponentOptions} prop={'viewOnly'} value={permissions.viewOnly} />
        <SwitchEdit key={'hide'} header={'Hide'} onChangeText={onChangeComponentOptions} prop={'hide'} value={permissions.hide} />

      </div>}

      {selectedComponent && <div style={{ marginRight: 10, backgroundColor: 'white', padding: 10, borderRadius: 4 }} >
        <div style={{ fontSize: 20, paddingBottom: 10, fontWeight: 500 }}>{'Preview'}</div>
        <TemplateSelector component={selectedComponent} key={'key' + selectedComponent.label} template={newTemplates[selectedTempIndex]} obj={fakeObject} onChangeText={onChangeFakeChange} />
      </div>}
    </div>
    }
    </div>
  </div >)
}

class CustomDiagram extends React.PureComponent {
  componentWillMount() {
    this.props.dispatchGetInvoiceTemplates();
  }

  render() {
    return <RenderView {...this.props} />;
  }
}

export default CustomDiagram;
